import React, { type ComponentPropsWithRef } from "react";
import { isNumber } from "@common/helpers/typeGuards";
import {
    AmountRoundingLevel,
    ROUNDING_LEVEL_LABELS,
} from "@common/types/transactionRule";
import { AmountFilterLevel } from "@common/constants/counterparty";
import { NumberFormat } from "@design-system/NumberFormat/NumberFormat";
import { Picker } from "@design-system/Picker/Picker.lib";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import { HStack } from "@design-system/HStack/HStack";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import { BaseFilterProps } from "../../lib";
import { cashDirectionRadios, DirectionRadioDef } from "./directionRadios";
import styles from "./TransactionAmountFilter.module.scss";

const AmountFilterInput = (
    props: Omit<ComponentPropsWithRef<"input">, "size">,
) => <Input {...props} size="xl" clearable={false} />;

type RoundingRadioValue = "any" | "rounded" | "notRounded";

interface RoundingRadioDef {
    id: string;
    label: string;
    value: RoundingRadioValue;
}

const roundingRadios: RoundingRadioDef[] = [
    {
        id: "transactions-filter-form__rounding__any",
        label: "Any",
        value: "any",
    },
    {
        id: "transactions-filter-form__rounding__rounded",
        label: "Rounded",
        value: "rounded",
    },
    {
        id: "transactions-filter-form__rounding__not_rounded",
        label: "Not rounded",
        value: "notRounded",
    },
];

const roundingLevelItems = Object.values(AmountRoundingLevel).map((level) => ({
    value: level,
    label: ROUNDING_LEVEL_LABELS[level],
}));

const getRoundingRadioValue = (
    amountRounded: boolean | undefined,
): RoundingRadioValue => {
    if (amountRounded === true) {
        return "rounded";
    }
    if (amountRounded === false) {
        return "notRounded";
    }
    return "any";
};

const AMOUNT_FILTER_LEVEL_RADIOS = [
    {
        id: "amount-filter-level__counterparty",
        label: "Counterparties",
        value: AmountFilterLevel.COUNTERPARTY,
    },
    {
        id: "amount-filter-level__journal-entry",
        label: "Journal entries",
        value: AmountFilterLevel.JOURNAL_ENTRY,
    },
] as const;

interface Props extends BaseFilterProps {
    directionSwitchDisabled?: boolean;
    directionSwitchReadonly?: boolean;
    directionRadios?: DirectionRadioDef[];
    showFilterLevel?: boolean;
}

export const TransactionAmountFilter: React.FC<Props> = ({
    filters,
    onChange,
    directionSwitchDisabled,
    directionSwitchReadonly,
    directionRadios = cashDirectionRadios,
    showFilterLevel,
}) => {
    const minAmountValue = isNumber(filters.minAmount) ? filters.minAmount : "";
    const maxAmountValue = isNumber(filters.maxAmount) ? filters.maxAmount : "";

    const currentRoundingRadio = getRoundingRadioValue(filters.amountRounded);
    const currentRoundingLevel =
        filters.amountRoundingLevel ?? AmountRoundingLevel.DOLLAR;

    const handleRoundingRadioChange = (value: RoundingRadioValue) => {
        if (value === "rounded") {
            onChange({
                amountRounded: true,
                amountRoundingLevel:
                    filters.amountRoundingLevel ?? AmountRoundingLevel.DOLLAR,
            });
            return;
        }
        if (value === "notRounded") {
            onChange({
                amountRounded: false,
                amountRoundingLevel: undefined,
            });
            return;
        }
        onChange({
            amountRounded: undefined,
            amountRoundingLevel: undefined,
        });
    };

    const currentFilterLevel =
        filters.amountFilterLevel ?? AmountFilterLevel.COUNTERPARTY;

    return (
        <div className={styles.container}>
            <div className="transactions-filter-form__amount amount-filter">
                {!directionSwitchDisabled && (
                    <section className={styles.direction}>
                        <header className={styles.header}>Direction</header>

                        <RadioGroup
                            value={filters.direction ?? ""}
                            onValueChange={(v) => {
                                const radio = directionRadios.find(
                                    ({ direction }) => (direction ?? "") === v,
                                );
                                if (radio) {
                                    onChange({ direction: radio.direction });
                                }
                            }}
                        >
                            {directionRadios.map(({ id, label, direction }) => (
                                <Radio
                                    key={id}
                                    value={direction ?? ""}
                                    label={label}
                                    disabled={directionSwitchReadonly}
                                />
                            ))}
                        </RadioGroup>
                    </section>
                )}

                <section>
                    <header className={styles.header}>Rounding</header>

                    <RadioGroup
                        value={currentRoundingRadio}
                        onValueChange={(v) => {
                            const radio = roundingRadios.find(
                                ({ value }) => value === v,
                            );
                            if (radio) {
                                handleRoundingRadioChange(radio.value);
                            }
                        }}
                    >
                        {roundingRadios.map(({ id, label, value }) => (
                            <Radio key={id} value={value} label={label} />
                        ))}
                    </RadioGroup>

                    {currentRoundingRadio === "rounded" && (
                        <div className={styles.roundingLevel}>
                            <Picker.Select
                                items={roundingLevelItems}
                                value={currentRoundingLevel}
                                onValueChange={(v) => {
                                    if (v) {
                                        onChange({
                                            amountRounded: true,
                                            amountRoundingLevel: v,
                                        });
                                    }
                                }}
                                placeholder="Select rounding level"
                            />
                        </div>
                    )}
                </section>

                {showFilterLevel && (
                    <section className={styles.filterLevel}>
                        <header className={styles.header}>Filter on</header>
                        <RadioGroup
                            value={currentFilterLevel}
                            onValueChange={(v) => {
                                const radio = AMOUNT_FILTER_LEVEL_RADIOS.find(
                                    ({ value }) => value === v,
                                );
                                if (radio) {
                                    onChange({
                                        amountFilterLevel:
                                            radio.value ===
                                            AmountFilterLevel.COUNTERPARTY
                                                ? undefined
                                                : radio.value,
                                    });
                                }
                            }}
                        >
                            {AMOUNT_FILTER_LEVEL_RADIOS.map(
                                ({ id, label, value }) => (
                                    <Radio
                                        key={id}
                                        value={value}
                                        label={label}
                                    />
                                ),
                            )}
                        </RadioGroup>
                    </section>
                )}

                <section className={styles.values}>
                    <header className={styles.header}>Value</header>
                    <HStack gap={24}>
                        <Field label="From" className={styles.valueField}>
                            <NumberFormat
                                value={minAmountValue}
                                onValueChange={(v) =>
                                    onChange({
                                        minAmount: v.floatValue ?? undefined,
                                    })
                                }
                                prefix="$ "
                                allowEmptyFormatting
                                customInput={AmountFilterInput}
                            />
                        </Field>

                        <Field label="To" className={styles.valueField}>
                            <NumberFormat
                                value={maxAmountValue}
                                onValueChange={(v) =>
                                    onChange({
                                        maxAmount: v.floatValue ?? undefined,
                                    })
                                }
                                prefix="$ "
                                allowEmptyFormatting
                                customInput={AmountFilterInput}
                            />
                        </Field>
                    </HStack>
                </section>
            </div>
        </div>
    );
};
